import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { MARKETING } from "@/lib/marketingContent";
import { VideraMark } from "@/components/VideraMark";

export const MarketingNav = ({ active }) => {
  const [open, setOpen] = React.useState(false);
  const nav = [
    { label: "Product", to: "/#product" },
    { label: "Assurance", to: "/#assurance" },
    { label: "Pricing", to: "/pricing" },
    { label: "FAQ", to: "/#faq" },
    { label: "Contact", to: "/contact" },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-[color:var(--marketing-border)] bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <Link to="/" data-testid="marketing-logo"
          className="inline-flex items-center gap-2.5 font-marketing-display text-lg font-black tracking-tight text-[color:var(--marketing-text)]">
          <VideraMark size={22} className="text-[color:var(--marketing-primary)]" />
          {MARKETING.product}
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {nav.map((n) => (
            <a key={n.label} href={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors hover:text-[color:var(--marketing-primary)] ${active === n.label ? "text-[color:var(--marketing-primary)]" : "text-[color:var(--marketing-text-muted)]"}`}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login" data-testid="nav-signin"
            className="text-sm font-semibold text-[color:var(--marketing-text)] transition-colors hover:text-[color:var(--marketing-primary)]">
            Sign in
          </Link>
          <Link to="/signup" data-testid="nav-trial"
            className="inline-flex items-center gap-1.5 rounded-sm bg-[color:var(--marketing-primary)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)]">
            Start free trial <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden" aria-label="Menu">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-[color:var(--marketing-border)] bg-white px-6 py-4 md:hidden" data-testid="nav-mobile-menu">
          {nav.map((n) => (
            <a key={n.label} href={n.to} onClick={() => setOpen(false)} data-testid={`nav-mobile-${n.label.toLowerCase()}`}
              className="block py-2 text-sm font-medium text-[color:var(--marketing-text-muted)]">{n.label}</a>
          ))}
          <Link to="/login" className="block py-2 text-sm font-semibold">Sign in</Link>
          <Link to="/signup" className="mt-2 block rounded-sm bg-[color:var(--marketing-primary)] px-4 py-2.5 text-center text-sm font-semibold text-white">
            Start free trial
          </Link>
        </div>
      )}
    </header>
  );
};

export const MarketingFooter = () => (
  <footer className="border-t border-[color:var(--marketing-border)] bg-white">
    <div className="mx-auto max-w-7xl px-6 py-14 lg:px-10">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <p className="inline-flex items-center gap-2.5 font-marketing-display text-lg font-black tracking-tight">
            <VideraMark size={20} className="text-[color:var(--marketing-primary)]" /> {MARKETING.product}
          </p>
          <p className="mt-2 font-marketing-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid="footer-tagline">
            {MARKETING.strapline}
          </p>
          <p className="mt-3 max-w-sm text-sm text-[color:var(--marketing-text-muted)]">{MARKETING.sub}</p>
          <address className="mt-6 space-y-0.5 text-xs not-italic text-slate-400" data-testid="footer-company-details">
            <div className="font-semibold text-slate-500">{MARKETING.company.name}</div>
            <div>{MARKETING.company.jurisdiction} · No. {MARKETING.company.number}</div>
            <div>{MARKETING.company.addressLines.join(", ")}</div>
          </address>
        </div>        <div>
          <p className="overline-label">Product</p>
          <div className="mt-3 space-y-2 text-sm text-[color:var(--marketing-text-muted)]">
            <a href="/#product" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Capabilities</a>
            <a href="/#assurance" className="block transition-colors hover:text-[color:var(--marketing-primary)]">AI assurance</a>
            <Link to="/pricing" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Pricing</Link>
          </div>
        </div>
        <div>
          <p className="overline-label">Get started</p>
          <div className="mt-3 space-y-2 text-sm text-[color:var(--marketing-text-muted)]">
            <Link to="/signup" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Start free trial</Link>
            <Link to="/login" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Sign in</Link>
            <Link to="/contact" data-testid="footer-contact" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Contact us</Link>
            <a href={`mailto:${MARKETING.salesEmail}`} className="block transition-colors hover:text-[color:var(--marketing-primary)]">
              {MARKETING.salesEmail}
            </a>
          </div>
        </div>
        <div>
          <p className="overline-label">Legal</p>
          <div className="mt-3 space-y-2 text-sm text-[color:var(--marketing-text-muted)]">
            <Link to="/legal/privacy" data-testid="footer-privacy" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Privacy &amp; data usage</Link>
            <Link to="/legal/terms" data-testid="footer-terms" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Terms of use</Link>
            <Link to="/legal/data-processing" data-testid="footer-dpa" className="block transition-colors hover:text-[color:var(--marketing-primary)]">Data processing</Link>
          </div>
        </div>
      </div>
      <p className="mt-12 border-t border-[color:var(--marketing-border)] pt-6 text-xs text-slate-400">
        © {new Date().getFullYear()} {MARKETING.legalName}. All rights reserved. Prices exclude VAT.
      </p>
    </div>
  </footer>
);

export const TrialCta = ({ dense = false }) => {
  const navigate = useNavigate();
  return (
    <div className={`flex flex-wrap items-center gap-3 ${dense ? "" : "mt-10"}`}>
      <button data-testid="cta-start-trial" onClick={() => navigate("/signup")}
        className="inline-flex items-center gap-2 rounded-sm bg-[color:var(--marketing-primary)] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[color:var(--marketing-primary-hover)]">
        Start 7-day free trial <ArrowRight size={16} strokeWidth={1.5} />
      </button>
      <button data-testid="cta-view-pricing" onClick={() => navigate("/pricing")}
        className="rounded-sm border-2 border-slate-200 px-6 py-3 text-sm font-semibold text-[color:var(--marketing-text)] transition-colors hover:border-slate-900 hover:bg-slate-50">
        View pricing
      </button>
    </div>
  );
};
